import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { Dialog, DialogTemplate, Snackbar, TextField, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { TestId } from '@modules/utilities/constants/testConstants';
import { FeedbackRating } from '@rbx/conv-ai-provider';
import FeedbackRatingIcon from './FeedbackRatingIcon';
import useShareFeedbackStyles from './ShareFeedback.styles';
import { SubmitFeedbackCallback } from '../context/ShareFeedbackProvider';

interface ShareRatedFeedbackDialogProps {
  rating: FeedbackRating;
  open: boolean;
  onClose: () => void;
  submitFeedback: SubmitFeedbackCallback;
}

const ShareRatedFeedbackDialog: FC<React.PropsWithChildren<ShareRatedFeedbackDialogProps>> = ({
  rating,
  open,
  onClose: closeDialog,
  submitFeedback,
}) => {
  const { translate } = useTranslation();
  const [comment, setComment] = useState<string>('');
  const {
    classes: { container, headerIcon, textarea },
  } = useShareFeedbackStyles();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const onChangeComment = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setComment(e.currentTarget.value);
  }, []);
  const commentPlaceholder = useMemo(
    () =>
      rating === FeedbackRating.Positive
        ? translate('Placeholder.SharePositiveFeedback')
        : translate('Placeholder.ShareNegativeFeedback'),
    [rating, translate],
  );
  const onConfirm = useCallback(() => {
    setShowSnackbar(true);
    submitFeedback(rating, comment);
    setComment('');
    closeDialog();
  }, [submitFeedback, rating, comment, closeDialog]);
  const onClose = useCallback(() => {
    // submit feedback silently
    submitFeedback(rating, '');
    setComment('');
    closeDialog();
  }, [rating, submitFeedback, closeDialog]);
  return (
    <React.Fragment>
      <Dialog
        tabIndex={-1}
        data-testid={TestId.FeedbackDialog}
        open={open}
        fullWidth
        maxWidth='XLarge'
        onClose={onClose}
        PaperProps={{
          className: container,
        }}>
        <DialogTemplate
          cancelText={translate('Action.Cancel')}
          color='primaryBrand'
          confirmText={translate('Label.SubmitFeedback')}
          content={
            <TextField
              id='assistant-feedback-comment'
              className={textarea}
              value={comment}
              autoFocus
              size='medium'
              variant='outlined'
              fullWidth
              multiline
              rows={5}
              label=''
              aria-label={commentPlaceholder}
              placeholder={commentPlaceholder}
              onChange={onChangeComment}
            />
          }
          onCancel={onClose}
          onConfirm={onConfirm}
          title={
            <Typography
              variant='h3'
              component='h3'
              display='flex'
              alignItems='center'
              justifyContent='center'>
              <FeedbackRatingIcon className={headerIcon} rating={rating} size={24} filled />
              {translate('Label.ShareFeedback')}
            </Typography>
          }
        />
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message={translate('Message.FeedbackSubmitted')}
        autoHideDuration={3000}
      />
    </React.Fragment>
  );
};

export default ShareRatedFeedbackDialog;
